import { CheckCircle2, Circle, Gift, Play, RotateCcw, Trophy } from 'lucide-react'
import { Link } from '@inertiajs/react'
import type { OnboardingStatus } from '@/types'

interface OnboardingChecklistProps {
  status: OnboardingStatus
  onStartTour: () => void
  onRestartTour: () => void
  isTourDismissed: boolean
  communitySlug?: string
}

interface ChecklistItem {
  id: string
  label: string
  description: string
  points: number
  completed: boolean
  href?: string
}

export function OnboardingChecklist({
  status,
  onStartTour,
  onRestartTour,
  isTourDismissed,
  communitySlug,
}: OnboardingChecklistProps) {
  const communityLabel = communitySlug === 'pharmaciens' ? 'Aller à la communauté' : 'Aller au Chat'
  const items: ChecklistItem[] = [
    {
      id: 'tour',
      label: 'Découvrir la plateforme',
      description: 'Faites le tour guidé pour découvrir les fonctionnalités',
      points: 5,
      completed: status.tour_completed,
    },
    {
      id: 'first_post',
      label: 'Publier votre premier message',
      description: 'Présentez-vous ou posez une question à la communauté',
      points: 5,
      completed: status.first_post_created,
      href: '/community',
    },
  ]

  const completedCount = items.filter((item) => item.completed).length
  const totalCount = items.length
  const progressPercentage = status.percentage

  // If onboarding is completed, show success message
  if (status.is_completed) {
    return (
      <section className="bg-gradient-to-br from-tessan-cream to-tessan-yellow/30 rounded-xl border border-tessan-yellow/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-6 h-6 text-yellow-600" />
          <h2 className="font-heading text-xl font-bold text-gray-900">
            Onboarding terminé !
          </h2>
        </div>
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tessan-green/10 mb-4">
            <CheckCircle2 className="w-10 h-10 text-tessan-green" />
          </div>
          <p className="text-gray-700 font-medium mb-2">
            Félicitations ! Vous avez complété l'onboarding
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Vous avez gagné le badge <strong>"Membre certifié"</strong> et 95 points !
          </p>
          <Link
            href="/account"
            className="inline-flex items-center gap-2 text-tessan-green font-medium hover:underline"
          >
            Voir mes badges
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Gift className="w-6 h-6 text-tessan-green" />
          <h2 className="font-heading text-xl font-bold text-gray-900">
            Bienvenue ! Complétez votre onboarding
          </h2>
        </div>
        <span className="text-sm font-medium text-gray-500">
          {completedCount}/{totalCount}
        </span>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-tessan-green rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {progressPercentage}% complété
        </p>
      </div>

      {/* Checklist items */}
      <div className="space-y-3 mb-6">
        {items.map((item) => (
          <div
            key={item.id}
            className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
              item.completed
                ? 'bg-tessan-cream/50 border-tessan-green/20'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            {item.completed ? (
              <CheckCircle2 className="w-5 h-5 text-tessan-green flex-shrink-0 mt-0.5" />
            ) : (
              <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`font-medium ${
                    item.completed ? 'text-tessan-green line-through' : 'text-gray-900'
                  }`}
                >
                  {item.label}
                </span>
                <span className="text-xs font-semibold text-tessan-green bg-tessan-cream px-2 py-1 rounded-full flex-shrink-0">
                  +{item.points} pts
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>

              {/* Action button for incomplete items */}
              {!item.completed && (
                <div className="mt-2">
                  {item.id === 'tour' ? (
                    <button
                      onClick={status.tour_completed ? onRestartTour : onStartTour}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-tessan-green hover:text-tessan-green/80 transition-colors"
                    >
                      {isTourDismissed ? (
                        <>
                          <RotateCcw size={14} />
                          Reprendre le tour
                        </>
                      ) : (
                        <>
                          <Play size={14} />
                          Commencer le tour
                        </>
                      )}
                    </button>
                  ) : item.href ? (
                    <Link
                      href={item.href}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-tessan-green hover:text-tessan-green/80 transition-colors"
                    >
                      <Play size={14} />
                      {communityLabel}
                    </Link>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Bonus info */}
      <div className="bg-tessan-yellow/20 rounded-lg p-4 border border-tessan-yellow/30">
        <div className="flex items-start gap-3">
          <Trophy className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">Bonus à débloquer</p>
            <p className="text-sm text-gray-600 mt-1">
              Complétez toutes les tâches pour recevoir <strong>+60 points bonus</strong> et le
              badge <strong>"Membre certifié"</strong> !
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
